// utils/seo.js

export const BRAND = { en: "Prism Digital", ar: "بريزم ديجيتال" };

// Adds " (Page N)" to a meta description and keeps the total within max chars.
export const withPageSuffix = (description, suffix, max = 155) => {
  const tail = ` (${suffix})`;
  const text = String(description || "").trim();
  if (text.length + tail.length <= max) return text + tail;
  const room = max - tail.length - 1;
  const cut = text.slice(0, room).replace(/\s+\S*$/, "");
  return `${cut}…${tail}`;
};

const prettify = (slug) =>
  String(slug)
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Find the category object that matches the ?cat= slug.
export const findCategory = (categories, cat) => {
  if (!cat) return null;
  return (
    (categories || []).find((c) =>
      [c?.slug, c?.value, c?.id, c?._id, c?.name]
        .filter(Boolean)
        .map(String)
        .includes(cat),
    ) || null
  );
};

// Readable category name (Arabic name on /ar), falling back to the prettified slug.
export const getCategoryName = (category, cat, lang = "en") => {
  if (!cat) return null;
  const name =
    lang === "ar" ? category?.name_ar || category?.name : category?.name;
  return name || category?.title || category?.label || prettify(cat);
};

// Category SEO fields. Case studies, portfolios and testimonials nest them under
// seo.<type>; podcast categories keep them flat on seo itself. Both work here.
// Returns null for any empty field so the page can fall back to its template.
export const getCategorySeo = (category, type, lang = "en") => {
  const seo = category?.seo || {};
  const block = seo[type] || seo;
  const pick = (key) => {
    const value = lang === "ar" ? block[`${key}_ar`] : block[key];
    return typeof value === "string" && value.trim() ? value.trim() : null;
  };
  return {
    h1: pick("h1"),
    title: pick("metaTitle"),
    description: pick("metaDesc"),
  };
};

// ---------------------------------------------------------------------------
// Fallback text per listing and language. Used only when the key is missing
// in the CURRENT language's translation file.
// ---------------------------------------------------------------------------
export const LISTING_FALLBACKS = {
  caseStudies: {
    en: {
      metaTitle: "Case Studies | Prism Digital",
      metaDescription:
        "Explore Prism Digital case studies and see the results we delivered for our clients.",
      categoryTitle: (c) => `${c} Case Studies | Prism Digital`,
      categoryDescription: (c) =>
        `Explore ${c} case studies from Prism Digital and see the results we delivered for brands in this industry.`,
      categoryPageTitle: (c) => `${c} Case Studies`,
      pageSuffix: (p) => `Page ${p}`,
      pageTitleBase: "Case Studies",
    },
    ar: {
      metaTitle: "دراسات الحالة | بريزم ديجيتال",
      metaDescription:
        "استكشف دراسات الحالة من بريزم ديجيتال واطّلع على النتائج التي حققناها لعملائنا.",
      categoryTitle: (c) => `دراسات حالة ${c} | بريزم ديجيتال`,
      categoryDescription: (c) =>
        `استكشف دراسات حالة ${c} من بريزم ديجيتال، واطّلع على النتائج التي حققناها للعلامات التجارية في هذا القطاع.`,
      categoryPageTitle: (c) => `دراسات حالة ${c}`,
      pageSuffix: (p) => `الصفحة ${p}`,
      pageTitleBase: "دراسات الحالة",
    },
  },

  portfolio: {
    en: {
      metaTitle: "Portfolio | Prism Digital",
      metaDescription:
        "Browse Prism Digital's portfolio and see the work we delivered for our clients.",
      categoryTitle: (c) => `${c} Portfolio | Prism Digital`,
      categoryDescription: (c) =>
        `Browse ${c} projects from Prism Digital and see the work we delivered for brands in this industry.`,
      categoryPageTitle: (c) => `${c} Portfolio`,
      pageSuffix: (p) => `Page ${p}`,
      pageTitleBase: "Portfolio",
    },
    ar: {
      metaTitle: "معرض الأعمال | بريزم ديجيتال",
      metaDescription:
        "تصفّح معرض أعمال بريزم ديجيتال واطّلع على المشاريع التي أنجزناها لعملائنا.",
      categoryTitle: (c) => `معرض أعمال ${c} | بريزم ديجيتال`,
      categoryDescription: (c) =>
        `تصفّح مشاريع ${c} من بريزم ديجيتال، واطّلع على الأعمال التي أنجزناها للعلامات التجارية في هذا القطاع.`,
      categoryPageTitle: (c) => `معرض أعمال ${c}`,
      pageSuffix: (p) => `الصفحة ${p}`,
      pageTitleBase: "معرض الأعمال",
    },
  },

  testimonials: {
    en: {
      metaTitle: "Digital Marketing Reviews | Prism Digital",
      metaDescription:
        "Watch client video reviews and hear what brands say about working with Prism Digital.",
      categoryTitle: (c) => `${c} Client Reviews | Prism Digital`,
      categoryDescription: (c) =>
        `Watch ${c} client video reviews and hear what brands say about working with Prism Digital.`,
      categoryPageTitle: (c) => `${c} Client Reviews`,
      pageSuffix: (p) => `Page ${p}`,
      pageTitleBase: "Digital Marketing Reviews",
    },
    ar: {
      metaTitle: "آراء العملاء في التسويق الرقمي | بريزم ديجيتال",
      metaDescription:
        "شاهد مراجعات العملاء بالفيديو واستمع إلى ما تقوله العلامات التجارية عن تجربة العمل مع بريزم ديجيتال.",
      categoryTitle: (c) => `آراء عملاء ${c} | بريزم ديجيتال`,
      categoryDescription: (c) =>
        `شاهد مراجعات عملاء ${c} بالفيديو واستمع إلى ما تقوله العلامات التجارية عن تجربة العمل مع بريزم ديجيتال.`,
      categoryPageTitle: (c) => `آراء عملاء ${c}`,
      pageSuffix: (p) => `الصفحة ${p}`,
      pageTitleBase: "آراء العملاء في التسويق الرقمي",
    },
  },

  clients: {
    en: {
      metaTitle: "Our Clients | Prism Digital",
      metaDescription:
        "Explore the brands that trust Prism Digital, a leading digital marketing agency in the UAE.",
      categoryTitle: (c) => `${c} Clients | Prism Digital`,
      categoryDescription: (c) =>
        `See the ${c} brands that trust Prism Digital, a leading digital marketing agency in the UAE.`,
      categoryPageTitle: (c) => `${c} Clients`,
      pageSuffix: (p) => `Page ${p}`,
      pageTitleBase: "Our Clients",
    },
    ar: {
      metaTitle: "عملاؤنا | بريزم ديجيتال",
      metaDescription:
        "تعرّف على العلامات التجارية التي تثق في بريزم ديجيتال، وكالة التسويق الرقمي الرائدة في الإمارات.",
      categoryTitle: (c) => `عملاء ${c} | بريزم ديجيتال`,
      categoryDescription: (c) =>
        `تعرّف على عملاء ${c} الذين يثقون في بريزم ديجيتال، وكالة التسويق الرقمي الرائدة في الإمارات.`,
      categoryPageTitle: (c) => `عملاء ${c}`,
      pageSuffix: (p) => `الصفحة ${p}`,
      pageTitleBase: "عملاؤنا",
    },
  },

  industries: {
    en: {
      metaTitle: "Digital Marketing Services by Industry | Prism Digital",
      metaDescription:
        "Whatever your industry, our team is ready to provide digital marketing services.",
      pageSuffix: (p) => `Page ${p}`,
      pageTitleBase: "Digital Marketing Services by Industry",
    },
    ar: {
      metaTitle: "خدمات التسويق الرقمي لمختلف القطاعات | بريزم ديجيتال",
      metaDescription:
        "أياً كان قطاعك، فريقنا جاهز لتقديم خدمات التسويق الرقمي التي تناسب أعمالك.",
      pageSuffix: (p) => `الصفحة ${p}`,
      pageTitleBase: "خدمات التسويق الرقمي حسب القطاع",
    },
  },

  blogs: {
    en: {
      metaTitle: "Blog | Prism Digital",
      metaDescription:
        "Read the latest digital marketing insights, tips and news from Prism Digital.",
      pageSuffix: (p) => `Page ${p}`,
      pageTitleBase: "Blog",
    },
    ar: {
      metaTitle: "المدونة | بريزم ديجيتال",
      metaDescription:
        "اقرأ أحدث المقالات والرؤى والنصائح في التسويق الرقمي من بريزم ديجيتال.",
      pageSuffix: (p) => `الصفحة ${p}`,
      pageTitleBase: "المدونة",
    },
  },

  events: {
    en: {
      metaTitle: "Events | Prism Digital",
      metaDescription:
        "Discover the events Prism Digital hosts and takes part in across the UAE.",
      pageSuffix: (p) => `Page ${p}`,
      pageTitleBase: "Events",
    },
    ar: {
      metaTitle: "الفعاليات | بريزم ديجيتال",
      metaDescription:
        "تعرّف على الفعاليات التي تنظمها بريزم ديجيتال وتشارك فيها في الإمارات.",
      pageSuffix: (p) => `الصفحة ${p}`,
      pageTitleBase: "الفعاليات",
    },
  },

  careers: {
    en: {
      metaTitle: "Careers | Prism Digital",
      metaDescription:
        "Explore open positions at Prism Digital and join our digital marketing team.",
      pageSuffix: (p) => `Page ${p}`,
      pageTitleBase: "Careers",
    },
    ar: {
      metaTitle: "الوظائف | بريزم ديجيتال",
      metaDescription:
        "اطّلع على الوظائف الشاغرة في بريزم ديجيتال وانضم إلى فريقنا في التسويق الرقمي.",
      pageSuffix: (p) => `الصفحة ${p}`,
      pageTitleBase: "الوظائف",
    },
  },
};

// ---------------------------------------------------------------------------
// One function for the whole title + description logic of a listing page:
// admin category SEO -> translation keys (current language only) -> fallbacks.
// ---------------------------------------------------------------------------
export const buildListingSeo = ({
  t,
  i18n,
  lang = "en",
  ns,
  prefix, // e.g. "caseStudies.seo"
  fb, // LISTING_FALLBACKS.<listing>[lang]
  page = 1,
  cat = null,
  categoryName = null,
  seo = {},
}) => {
  const brand = BRAND[lang] || BRAND.en;

  // Use the translation only if it exists in the current language, otherwise our
  // own fallback. This skips i18next's fallbackLng, which would return English.
  const tr = (key, vars, fallback) =>
    i18n.getResource(lang, ns, `${prefix}.${key}`)
      ? t(`${prefix}.${key}`, vars)
      : fallback;

  let title;
  let description;

  if (cat && fb.categoryTitle) {
    title =
      seo.title ||
      tr(
        "categoryTitle",
        { category: categoryName },
        fb.categoryTitle(categoryName),
      );
    description =
      seo.description ||
      tr(
        "categoryDescription",
        { category: categoryName },
        fb.categoryDescription(categoryName),
      );
  } else {
    title = tr("metaTitle", {}, fb.metaTitle);
    description = tr("metaDescription", {}, fb.metaDescription);
  }

  if (page > 1) {
    const suffix = tr("pageSuffix", { page }, fb.pageSuffix(page));
    const titleBase = tr("pageTitleBase", {}, fb.pageTitleBase);

    if (cat && fb.categoryPageTitle) {
      title = seo.title
        ? `${seo.title} – ${suffix}`
        : `${fb.categoryPageTitle(categoryName)} – ${suffix} | ${brand}`;
    } else {
      title = `${titleBase} – ${suffix} | ${brand}`;
    }
    description = withPageSuffix(description, suffix);
  }

  return { title, description };
};
